import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaEye, FaSearch, FaBuilding, FaEnvelope, FaPhone } from 'react-icons/fa';
import { Modal, Button } from 'react-bootstrap';
import '../pages_premium.css';
import AdminPagination from '../components/common/AdminPagination';

function HiremateCandidateRequestsList() {
    const { candidateId } = useParams();
    const navigate = useNavigate();

    const [candidateData, setCandidateData] = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const fetchCandidateRequests = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/admin/hiremates/${candidateId}/requests`);
            if (res.data) {
                setCandidateData(res.data.candidate || null);
                setRequests(Array.isArray(res.data.requests) ? res.data.requests : []);
            }
        } catch (error) {
            console.error('Error fetching candidate requests:', error);
            toast.error('Failed to fetch candidate job requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidateRequests();
    }, [candidateId]);

    const parseJSON = (val, fallback = []) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        try {
            return JSON.parse(val);
        } catch (e) {
            return fallback;
        }
    };

    const renderBadges = (val, colorScheme = 'blue') => {
        const parsed = parseJSON(val, Array.isArray(val) ? val : (typeof val === 'string' && val ? [val] : []));
        const list = Array.isArray(parsed) ? parsed : (typeof parsed === 'object' && parsed ? Object.keys(parsed) : [String(parsed)]);
        
        if (!list || list.length === 0) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>-</span>;

        const bg = colorScheme === 'blue' ? '#e0f2fe' : (colorScheme === 'green' ? '#dcfce7' : '#f3e8ff');
        const color = colorScheme === 'blue' ? '#0369a1' : (colorScheme === 'green' ? '#15803d' : '#6b21a8');

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {list.map((item, idx) => (
                    <span key={idx} style={{
                        backgroundColor: bg,
                        color: color,
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        whiteSpace: 'nowrap'
                    }}>
                        {typeof item === 'object' ? (item.title || item.name || JSON.stringify(item)) : String(item)}
                    </span>
                ))}
            </div>
        );
    };

    const getStatusBadge = (status) => {
        let bg = '#f1f5f9';
        let color = '#475569';
        const s = String(status || '').toLowerCase();

        if (s === 'invited') {
            bg = '#fef3c7'; color = '#d97706';
        } else if (s === 'applied' || s === 'under review') {
            bg = '#e0f2fe'; color = '#0369a1';
        } else if (s === 'selected' || s === 'trial') {
            bg = '#e0e7ff'; color = '#4338ca';
        } else if (s === 'confirmed' || s === 'hired' || s === 'completed') {
            bg = '#dcfce7'; color = '#15803d';
        } else if (s === 'declined' || s === 'not suitable' || s === 'no show') {
            bg = '#fee2e2'; color = '#b91c1c';
        } else if (s === 'backup') {
            bg = '#f3e8ff'; color = '#7e22ce';
        }

        return (
            <span style={{
                backgroundColor: bg,
                color: color,
                padding: '4px 10px',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '12px',
                textTransform: 'capitalize',
                display: 'inline-block',
                whiteSpace: 'nowrap'
            }}>
                {status || 'Unknown'}
            </span>
        );
    };

    const filteredRequests = requests.filter(req => {
        const title = req.job_title || '';
        const company = req.company_name || '';
        const poster = req.poster_name || '';
        const query = search.toLowerCase();
        return title.toLowerCase().includes(query) || company.toLowerCase().includes(query) || poster.toLowerCase().includes(query);
    });

    const paginatedRequests = filteredRequests.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const userObj = candidateData?.Provider || {};

    return (
        <div className="page-premium-wrapper hiremate-requests-list-page">
            <div className="page-premium-content">

                {/* Navigation Back Button */}
                <div style={{ marginBottom: '16px' }}>
                    <button
                        onClick={() => navigate('/hiremates')}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    >
                        <FaArrowLeft /> Back to Candidates List
                    </button>
                </div>

                {/* Candidate Summary Banner */}
                {candidateData && (
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '20px 24px',
                        marginBottom: '24px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {candidateData.profile_photo || userObj.avatar ? (
                                <img
                                    src={candidateData.profile_photo || userObj.avatar}
                                    alt={userObj.name}
                                    style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #0ab68e' }}
                                />
                            ) : (
                                <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#0ab68e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '22px' }}>
                                    {userObj.name ? userObj.name.charAt(0).toUpperCase() : 'H'}
                                </div>
                            )}
                            <div>
                                <h3 style={{ margin: 0, fontWeight: '800', color: '#0f172a', fontSize: '1.25rem' }}>{userObj.name || 'Candidate Profile'}</h3>
                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <span><FaEnvelope style={{ color: '#94a3b8' }} /> {userObj.email || '-'}</span>
                                    <span><FaPhone style={{ color: '#94a3b8' }} /> {userObj.phone || '-'}</span>
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <div>{renderBadges(candidateData.categories, 'blue')}</div>
                                <div>{renderBadges(candidateData.roles, 'purple')}</div>
                            </div>

                            <span style={{
                                backgroundColor: '#e0f2fe',
                                color: '#0369a1',
                                padding: '8px 16px',
                                borderRadius: '12px',
                                fontWeight: '800',
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}>
                                {requests.length} Total Requests / Invites
                            </span>
                        </div>
                    </div>
                )}

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Candidate Job Requests & Direct Invites
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by job title, business owner, or company..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        {loading ? (
                            <div className="text-center py-5 text-secondary">Loading candidate requests...</div>
                        ) : (
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th style={{ minWidth: '220px' }}>Job Title & Role</th>
                                        <th style={{ minWidth: '220px' }}>Business Owner / Company</th>
                                        <th style={{ minWidth: '160px' }}>Request Type</th>
                                        <th style={{ minWidth: '140px' }}>Request Date</th>
                                        <th style={{ minWidth: '130px' }}>Status</th>
                                        <th style={{ textAlign: 'center', width: '100px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRequests.map((req, index) => (
                                        <tr key={index}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + index + 1}</td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{req.job_title}</strong>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>{req.role} ({req.category})</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <strong style={{ color: '#334155', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                         <FaBuilding style={{ color: '#0ab68e' }} /> {req.company_name}
                                                    </strong>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>{req.poster_name}</span>
                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>{req.poster_email}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{
                                                    backgroundColor: req.request_type.includes('Direct Invite') ? '#f3e8ff' : '#e0f2fe',
                                                    color: req.request_type.includes('Direct Invite') ? '#6b21a8' : '#0369a1',
                                                    padding: '4px 10px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    display: 'inline-block',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {req.request_type}
                                                </span>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: '13px', color: '#334155', fontWeight: '500' }}>
                                                    {req.request_date ? new Date(req.request_date).toLocaleDateString() : '-'}
                                                </span>
                                            </td>
                                            <td>
                                                {getStatusBadge(req.status)}
                                            </td>
                                            <td>
                                                <div className="hire-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => { setSelectedRequest(req); setShowModal(true); }}
                                                        className="action-btn action-btn-view"
                                                        data-tooltip="View Details"
                                                        style={{ backgroundColor: '#0ab68e', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredRequests.length === 0 && (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="ptable-empty">
                                                    <div className="ptable-empty-icon">📨</div>
                                                    <p>No job requests or direct invitations found for this candidate.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filteredRequests.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="requests"
                    />
                </div>

            </div>

            {/* Request Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <Modal.Title style={{ fontSize: '1.2rem', fontWeight: '700', color: '#0f172a' }}>
                        Job Request & Invitation Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    {selectedRequest && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                                <div>
                                    <h4 style={{ margin: 0, fontWeight: '700', color: '#0f172a' }}>{selectedRequest.job_title}</h4>
                                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>{selectedRequest.role} • {selectedRequest.category}</p>
                                </div>
                                {getStatusBadge(selectedRequest.status)}
                            </div>

                            <div className="admin-2col-grid" style={{ gap: '16px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px' }}>
                                    <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '8px' }}>Business Owner / Employer</h6>
                                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#334155' }}><strong>Company:</strong> {selectedRequest.company_name}</p>
                                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#334155' }}><strong>Owner:</strong> {selectedRequest.poster_name}</p>
                                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#334155' }}><strong>Email:</strong> {selectedRequest.poster_email}</p>
                                </div>

                                <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px' }}>
                                    <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '8px' }}>Request Meta</h6>
                                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#334155' }}><strong>Request Type:</strong> {selectedRequest.request_type}</p>
                                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#334155' }}><strong>Date:</strong> {selectedRequest.request_date ? new Date(selectedRequest.request_date).toLocaleString() : '-'}</p>
                                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#334155' }}><strong>Budget:</strong> ${parseFloat(selectedRequest.budget || 0).toFixed(2)}</p>
                                </div>
                            </div>

                            {selectedRequest.cover_message && (
                                <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px' }}>
                                    <h6 style={{ fontWeight: '700', color: '#334155', marginBottom: '8px' }}>Application Cover Message</h6>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#475569', whiteSpace: 'pre-line' }}>{selectedRequest.cover_message}</p>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e2e8f0', padding: '12px 24px' }}>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close Details
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default HiremateCandidateRequestsList;
